import { Location } from '@angular/common';
import { VOLUNTEERWORKS_VALIDATION_CONTEXT } from './volunteer-work.validation';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormBuilder, FormArray, Validators, FormControl, AbstractControl } from '@angular/forms';
import { WorkExperienceService } from '../../work-experience.service';
import { ValidationService } from '../../../../../../shared/components/form-control/validation.service';
import { ICanDeactivate } from '../../../../../../core/guards/deactivate.interface';
import { ConfirmService } from '../../../../../../shared/components/confirm-box/confirm-box.service';
import { MainService } from '../../../../../../core/services/main.service';
import { EditWorkExperienceService } from '../edit-work-experience.service';


const VOLUNTEERFORM_VALIDATORS_SAVE = {
  jobStartDate: [Validators.required, ValidationService.monthYearValidator, ValidationService.futureDate, ValidationService.pastYear(80)],
  jobEndDate: [ValidationService.monthYearValidator,
    ValidationService.invalidEndDateOrCurrent('jobStartDate', 'jobEndDate', 'isJobCurrent'),
    ValidationService.invalidEndDateAndCurrent('jobEndDate', 'isJobCurrent'),
    ValidationService.futureDate],
  otherSkillDescription: [Validators.required, Validators.maxLength(200), Validators.pattern('[a-zA-Z0-9\\-\\s\&\(\)\/\.\'\,\"\:\;]*')],
  employerName: [Validators.required, Validators.maxLength(100)],
  jobLocation: [Validators.required],
  jobTitle: [Validators.required, Validators.maxLength(100)],
}

const VOLUNTEERFORM_VALIDATORS_DRAFT = {
  jobStartDate: [ValidationService.monthYearValidator],
  jobEndDate: [ValidationService.monthYearValidator],
  otherSkillDescription: [Validators.maxLength(200)],
}

const SKILL_DESCRIPTION_VALIDATORS = {
  answers: [ValidationService.minSelected(3), ValidationService.maxSelected(3)],
}

@Component({
  selector: 'vc-volunteer-work',
  templateUrl: './volunteer-work.component.html',
  styleUrls: ['./volunteer-work.component.scss']
})
export class VolunteerWorkComponent implements OnInit, ICanDeactivate, OnDestroy {

  volunteerForm: FormGroup;
  createAnother = new FormControl('N');
  jobTitle: any = [];
  jobLocation: any = [];

  endDateDisabled = false;

  constructor(
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly main: MainService,
    private readonly route: ActivatedRoute,
    private readonly confirm: ConfirmService,
    private readonly validation: ValidationService,
    private readonly workService: WorkExperienceService,
    private readonly editWEService: EditWorkExperienceService,
    private readonly location: Location,
  ) {
    this.validation.setContext(VOLUNTEERWORKS_VALIDATION_CONTEXT);
    this.main.pageTitle = this.route.snapshot.data.title;
    this.main.pageMainTitle = 'Work experience';
    this.initForm();
    this.route.queryParams.subscribe(params => {
      this.loadSkillsAndResolve(params.id);
    });
    this.editWEService.setFormGroups([this.volunteerForm, this.volunteerForm.get('skillDescription') as FormGroup]);
  }
  ngOnDestroy(): void {
    this.main.pageMainTitle = '';
  }
  ngOnInit() {
    this.volunteerForm.get('skillDescription').get('answers').valueChanges
      .subscribe((data: any[]) => {
        const filtered = data.filter(each => each.checked);
        if (filtered.find(each => each.id === '20')) {
          this.volunteerForm.addControl('otherSkillDescription', this.fb.control(''));
        } else {
          this.volunteerForm.removeControl('otherSkillDescription');
        }
      });
    this.volunteerForm.get('jobEndDate').valueChanges
      .subscribe(data => {
        this.volunteerForm.updateValueAndValidity();
      });

    this.volunteerForm.get('jobStartDate').valueChanges.subscribe(data => {
      this.volunteerForm.get('jobEndDate').updateValueAndValidity();
    });
    this.volunteerForm.get('isJobCurrent').valueChanges.subscribe(data => {
      this.volunteerForm.get('jobEndDate').updateValueAndValidity();
    });
  }

  initForm() {
    this.volunteerForm = this.fb.group({
      id: this.fb.control(''),
      employerName: this.fb.control(''),
      jobTitle: this.fb.control(''),
      isJobCurrent: this.fb.control(false),
      jobStartDate: this.fb.control(''),
      jobEndDate: this.fb.control(''),
      skillDescription: this.fb.group({
        question: this.fb.control('What did you do?'),
        answers: this.fb.array([]),
      })
    });
  }

  async loadSkillsAndResolve(id: any) {
    await this.loadAllSkills();
    if (id) {
      this.resolveData(id);
    }
  }

  resolveData(id: number): void {
    this.workService.getVolunteerExperienceDetails(id)
      .subscribe(data => {
        this.volunteerForm.get('id').setValue(data.id);
        this.volunteerForm.get('employerName').setValue(data.employerName);
        this.volunteerForm.get('jobTitle').setValue(data.jobTitle);
        this.volunteerForm.get('isJobCurrent').setValue(data.isJobCurrent);
        this.volunteerForm.get('jobStartDate').setValue(data.jobStartDate);
        this.volunteerForm.get('jobEndDate').setValue(data.jobEndDate);
        this.resolveSelectedSkills(data.skillDescription);
        if (data.skillDescription && data.skillDescription.includes('20')) {
          this.volunteerForm.addControl('otherSkillDescription', this.fb.control(''));
          this.volunteerForm.get('otherSkillDescription').setValue(data.otherSkillDescription);
        }
      });
  }

  resolveSelectedSkills(ids: string[]) {
    ((this.volunteerForm.get('skillDescription') as FormGroup)
      .get('answers') as FormArray).controls.forEach(each => {
        if (ids.includes(each.get('id').value)) {
          each.get('checked').setValue(true);
        }
      });
  }

  async loadAllSkills() {
    await this.workService.getAllVolunteerSkills()
      .then(res => this.loadAnswers(res));
  }

  loadAnswers(answers: any[]) {
    const farr = this.volunteerForm.get('skillDescription').get('answers') as FormArray;
    this.resetFormArr(farr);
    answers.map(answer => {
      return {
        id: answer.identifier,
        text: answer.description,
        checked: false,
      }
    }).forEach(answer => {
      farr.push(this.singleAnswerForm(answer));
    });
  }

  singleAnswerForm(data: any) {
    return this.fb.group({
      id: this.fb.control(data.id),
      text: this.fb.control(data.text),
      checked: this.fb.control(data.checked),
    });
  }

  saveAndContinue() {
    this.setValidators(this.volunteerForm, VOLUNTEERFORM_VALIDATORS_SAVE);
    this.setValidators(this.volunteerForm.get('skillDescription') as FormGroup, SKILL_DESCRIPTION_VALIDATORS);
    if (this.volunteerForm.valid) {
      const data = this.parseSaveData();
      this.workService.saveAndContinueVolunteerWork(data)
        .subscribe(res => {
          this.createNewWorkExp();
        });
    }
    window.scrollTo(0, 0);
  }

  createNewWorkExp() {
    this.resetConditionalForm();
    if (this.createAnother.value === 'N') {
      this.router.navigate(['/employment/cv-builder/guided/experience']);
    } else {
      this.createAnother.setValue('N');
      this.router.navigate([], { queryParams: {} });
    }
  }

  saveAndLater() {
    this.clearAllValidatorsAndErrors(this.volunteerForm);
    this.clearAllValidatorsAndErrors(this.volunteerForm.get('skillDescription') as FormGroup);
    this.setValidators(this.volunteerForm, VOLUNTEERFORM_VALIDATORS_DRAFT);
    if (this.volunteerForm.valid) {
      const data = this.parseSaveData();
      if (this.isEmptyForm(data)) {
        this.router.navigate(['/employment/cv-builder/guided/experience']);
      } else {
        this.workService.saveAndLaterVolunteerWork(data)
        .subscribe(res => {
          this.createNewWorkExp();
        });
    }
    }
    window.scrollTo(0, 0);
  }

  isEmptyForm(data) {
    const filterData = Object.keys(data).filter(name => (data[name]));
    if (filterData.length === 1 && filterData[0] === 'skillDescription') {
      const skillDescription =  filterData[0];
      return data[skillDescription].length === 0;
    }
    return false;
  }

  parseSaveData() {
    const volunteerWorks = this.volunteerForm.value;
    const skills = volunteerWorks.skillDescription.answers
      .filter(each => each.checked)
      .map(answer => answer.id);
    volunteerWorks.skillDescription = skills;
    return volunteerWorks;
  }

  resetConditionalForm() {
    this.initForm();
    this.loadAllSkills();
  }

  canExit() {
    if (this.volunteerForm.dirty) {
      this.confirm.confirm({
        header: 'Confirm',
        message: 'Are you sure you want to leave without saving changes?',
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        accept: () => {
          this.confirm.choose(true);
        },
        reject: () => {
          this.confirm.choose(false);
        }
      });
      return this.confirm.navigateSelection;
    }
    return true;
  }

  cvSummaryPage() {
    this.router.navigate(['/employment/cv-builder/guided']);
  }

  clearAllValidatorsAndErrors(form: FormGroup): void {
    Object.keys(form.controls).forEach(name => {
      const control = form.get(name);
      if (control) {
        control.clearValidators();
        control.setErrors(null);
        control.markAsTouched();
      }
    });
  }

  setValidators(form: FormGroup, validators: any): void {
    Object.keys(form.controls).forEach(name => {
      if (validators[name]) {
        const control = form.get(name);
        if (control) {
          control.setValidators(validators[name]);
          control.updateValueAndValidity();
          control.markAsTouched();
        }
      }
    });
  }

  onKeyJob(event: any) {
    const actualVal = event.target.value;
    const letters = /^[A-Za-z]+$/;
    if (actualVal.match(letters)) {
      this.workService.getJobTitles(event.target.value).subscribe(response => {
        this.jobTitle = response;
      });
    }
  }

  onKeyLocation(event: any) {
    const actualLoc = event.target.value;
    const allowedletters = /^[A-Za-z]+$/;
    if (actualLoc.match(allowedletters)) {
      this.workService.getJobLocation(event.target.value).subscribe(response => {
        this.jobLocation = response;
      });
    }
  }

  resetFormArr(farr: FormArray) {
    while (farr.controls.length !== 0) {
      farr.removeAt(0);
    }
  }
  gotoPrev() {
    this.location.back();
  }
}
